import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Send } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { api } from "@/api";
import { SITE } from "@/config";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";

export default function BlogPage() {
  const { lang, t } = useLanguage();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/posts").then((r) => setPosts(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  return (
    <main className="pt-28 pb-20" data-testid="blog-page">
      <Seo page="blog" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="max-w-2xl mb-14">
          <p className="text-xs font-mono uppercase tracking-[0.25em] text-terra mb-4">{t.blog.eyebrow}</p>
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight" data-testid="blog-title">{t.blog.name}</h1>
          <p className="text-muted2 mt-4 leading-relaxed">{t.blog.subtitle}</p>
        </Reveal>
        {!loading && posts.length === 0 && (
          <Reveal delay={0.1}>
            <div className="bg-white rounded-3xl border border-line p-10 sm:p-14 text-center max-w-2xl" data-testid="blog-empty">
              <p className="text-muted2 mb-6">{t.blog.empty}</p>
              <div className="flex flex-wrap justify-center gap-3">
                <a href={SITE.socials.instagram} target="_blank" rel="noreferrer" data-testid="blog-social-instagram" className="inline-flex items-center gap-2 rounded-full border border-line px-5 py-2.5 text-sm font-semibold hover:border-terra hover:text-terra transition-colors">
                  <Instagram className="w-4 h-4" /> Instagram
                </a>
                {SITE.socials.telegram && (
                  <a href={SITE.socials.telegram} target="_blank" rel="noreferrer" data-testid="blog-social-telegram" className="inline-flex items-center gap-2 rounded-full border border-line px-5 py-2.5 text-sm font-semibold hover:border-terra hover:text-terra transition-colors">
                    <Send className="w-4 h-4" /> Telegram
                  </a>
                )}
              </div>
            </div>
          </Reveal>
        )}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {posts.map((p, i) => {
            const tr = p.translations?.[lang] || p.translations?.ru || p.translations?.cs || {};
            return (
              <Reveal key={p.id} delay={i * 0.06}>
                <Link to={`/${lang}/blog/${p.slug}`} className="group block" data-testid={`blog-list-card-${p.slug}`}>
                  {p.cover_image && (
                    <div className="overflow-hidden rounded-2xl mb-5 border border-line">
                      <img src={p.cover_image} alt={tr.title} className="w-full h-56 object-cover transition-transform duration-700 group-hover:scale-105" />
                    </div>
                  )}
                  <p className="text-xs font-mono uppercase tracking-widest text-terra mb-2">{new Date(p.created_at).toLocaleDateString(lang === "cs" ? "cs-CZ" : lang === "ru" ? "ru-RU" : "en-GB", { day: "numeric", month: "long", year: "numeric" })} · {p.read_minutes} {t.blog.min}</p>
                  <h2 className="font-serif text-2xl font-semibold leading-snug group-hover:text-sage transition-colors">{tr.title}</h2>
                  <p className="text-sm text-muted2 mt-2 line-clamp-3">{tr.excerpt}</p>
                  <span className="inline-block mt-4 text-sm font-semibold text-sage group-hover:text-terra transition-colors">{t.blog.read} →</span>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </div>
    </main>
  );
}
